/**
 * Asset details editor - child of InfoPanel. Has data processing.
 */
import * as JQuery from "jquery";
import { ConstruktedAssetType } from "../types/common";
import type { ParsedConstruktedAjax } from "../types/common";
import { BasePanel } from "./BasePanel";
import type { PanelContainer } from "./BasePanel";

interface FormElements {
    viewAccess: HTMLInputElement;
    title: HTMLInputElement;
    captureDate: HTMLInputElement;
    tags: HTMLInputElement;
    downloadAccess: HTMLInputElement;
    coordinateSystem: HTMLInputElement;
    status: HTMLInputElement;
    assetPassword: HTMLInputElement;
    software: HTMLSelectElement;
}

interface FormDataType {
    action: string;
    post_id: string | undefined;
    title: string;
    description: string;
    captureDate: string;
    categories: string[];
    tags: string;
    viewAccess: string;
    downloadAccess: string;
    coordinateSystem: string;
    status: string;
    assetPassword?: string;
    software: string;
}

interface AjaxResponse {
    success: boolean;
    data: {
        message: string;
        new_status: string;
        title: string;
        description: string;
        date_capture: string;
        view_access: string;
        download_access: string;
        tags: string[];
        categories: string[];
        software: string;
    };
}

interface AssetDetailsSnapshot {
    title: string;
    description: string;
    captureDate: string;
    categories: string[];
    tags: string;
    viewAccess: string;
    downloadAccess: string;
    coordinateSystem: string;
    status: string;
    assetPassword: string;
    software: string;
}

export class AssetDetailsEditorPanel extends BasePanel {
    private _container: ReturnType<typeof window.jQuery> | null = null;

    private _CONSTRUKTED_AJAX!: ParsedConstruktedAjax;

    private _initialStatus: string | undefined;

    private _initialSnapshot: AssetDetailsSnapshot | null = null;

    getId(): string {
        return "asset-details-editor";
    }

    getContainer(): PanelContainer {
        if (!this._container) {
            this._container = window.jQuery("#construkted-popup-asset-editor");
        }

        return this._container;
    }

    protected onInit(): void {
        this._CONSTRUKTED_AJAX = window.CONSTRUKTED_AJAX;
        this._initializeSoftwareSelector();
        this._bindEvents();
    }

    protected onShow(): void {
        const statusElement = document.querySelector<HTMLInputElement>('input[name="asset-status"]:checked');

        this._initialStatus = statusElement?.value;
        this._handleViewAccessChange();

        // Snapshot editor state so we can detect dirtiness and restore on discard.
        this._initialSnapshot = this._takeSnapshot();
    }

    hasUnsavedChanges(): boolean {
        if (!this._initialSnapshot) {
            return false;
        }

        const currentSnapshot = this._takeSnapshot();

        return this._snapshotKey(currentSnapshot) !== this._snapshotKey(this._initialSnapshot);
    }

    discard(): void {
        if (!this._initialSnapshot) {
            return;
        }

        this._applySnapshot(this._initialSnapshot);
        // Ensure dirtiness is cleared even if normalization differs.
        this._initialSnapshot = this._takeSnapshot();
    }

    confirmDiscardChanges(): boolean {
        const ok = window.confirm("You have unsaved changes. Are you sure you want to leave?");

        if (ok) {
            this.discard();
        }

        return ok;
    }

    private _snapshotKey(snapshot: AssetDetailsSnapshot): string {
        // Ensure deterministic ordering for comparisons.
        const normalized: AssetDetailsSnapshot = {
            ...snapshot,
            title: snapshot.title.trim(),
            description: snapshot.description.trim(),
            captureDate: snapshot.captureDate.trim(),
            categories: [...snapshot.categories].map((c) => c.trim()).sort(),
            tags: snapshot.tags.trim(),
            viewAccess: snapshot.viewAccess.trim(),
            downloadAccess: snapshot.downloadAccess.trim(),
            coordinateSystem: snapshot.coordinateSystem.trim(),
            status: snapshot.status.trim(),
            assetPassword: snapshot.assetPassword.trim(),
            software: snapshot.software.trim()
        };

        return JSON.stringify(normalized);
    }

    private _takeSnapshot(): AssetDetailsSnapshot {
        const title = (document.getElementById("asset-title") as HTMLInputElement | null)?.value ?? "";
        const description = window.tinymce.get("asset-description")?.getContent?.() ?? "";

        const captureDate = (document.getElementById("asset-capture-date") as HTMLInputElement | null)?.value ?? "";

        const categories = AssetDetailsEditorPanel._getSelectedCategories().slice();

        const tags = (document.getElementById("asset-tags") as HTMLInputElement | null)?.value ?? "";

        const viewAccess = document.querySelector<HTMLInputElement>('input[name="view-access"]:checked')?.value ?? "";
        const downloadAccess =
            document.querySelector<HTMLInputElement>('input[name="download-access"]:checked')?.value ?? "";
        const coordinateSystem =
            document.querySelector<HTMLInputElement>('input[name="coordinate-system"]:checked')?.value ?? "";
        const status = document.querySelector<HTMLInputElement>('input[name="asset-status"]:checked')?.value ?? "";

        const assetPassword = (document.getElementById("asset-password") as HTMLInputElement | null)?.value ?? "";

        const software = (document.getElementById("software-selector") as HTMLSelectElement | null)?.value ?? "";

        return {
            title: title.trim(),
            description: description.trim(),
            captureDate: captureDate.trim(),
            categories: categories,
            tags: tags.trim(),
            viewAccess: viewAccess.trim(),
            downloadAccess: downloadAccess.trim(),
            coordinateSystem: coordinateSystem.trim(),
            status: status.trim(),
            assetPassword: assetPassword.trim(),
            software: software.trim()
        };
    }

    private _applySnapshot(snapshot: AssetDetailsSnapshot): void {
        const titleInput = document.getElementById("asset-title") as HTMLInputElement | null;

        if (titleInput) {
            titleInput.value = snapshot.title;
        }

        const captureDateInput = document.getElementById("asset-capture-date") as HTMLInputElement | null;

        if (captureDateInput) {
            captureDateInput.value = snapshot.captureDate;
        }

        const tagsInput = document.getElementById("asset-tags") as HTMLInputElement | null;

        if (tagsInput) {
            tagsInput.value = snapshot.tags;
        }

        const softwareSelector = document.getElementById("software-selector") as HTMLSelectElement | null;

        if (softwareSelector) {
            softwareSelector.value = snapshot.software;
        }

        // Restore view access radio group
        const publicAccess = document.getElementById("public-access") as HTMLInputElement | null;
        const privateAccess = document.getElementById("private-access") as HTMLInputElement | null;
        const passwordAccess = document.getElementById("password-access") as HTMLInputElement | null;

        if (publicAccess && privateAccess && passwordAccess) {
            publicAccess.checked = snapshot.viewAccess === "public";
            privateAccess.checked = snapshot.viewAccess === "private";
            passwordAccess.checked = snapshot.viewAccess === "password";
        }

        // Restore password value (even if currently hidden, keep it consistent for later edits).
        const assetPasswordInput = document.getElementById("asset-password") as HTMLInputElement | null;

        if (assetPasswordInput) {
            assetPasswordInput.value = snapshot.assetPassword;
        }

        // Restore download access radio group
        const publicDownload = document.getElementById("public-download-access") as HTMLInputElement | null;
        const privateDownload = document.getElementById("private-download-access") as HTMLInputElement | null;

        if (publicDownload && privateDownload) {
            publicDownload.checked = snapshot.downloadAccess === "allow_download";
            privateDownload.checked = snapshot.downloadAccess === "view_only";
        }

        // Restore coordinate system radio group
        const localSystem = document.getElementById("local-system") as HTMLInputElement | null;
        const wgs84System = document.getElementById("WGS84-system") as HTMLInputElement | null;
        const crsSystem = document.getElementById("CRS-system") as HTMLInputElement | null;

        if (localSystem && wgs84System && crsSystem) {
            localSystem.checked = snapshot.coordinateSystem === "0";
            wgs84System.checked = snapshot.coordinateSystem === "1";
            crsSystem.checked = snapshot.coordinateSystem === "2";
        }

        // Restore asset status radio group
        const draftStatus = document.getElementById("draft-status") as HTMLInputElement | null;
        const publishedStatus = document.getElementById("published-status") as HTMLInputElement | null;

        if (draftStatus && publishedStatus) {
            draftStatus.checked = snapshot.status === "0";
            publishedStatus.checked = snapshot.status === "1";
        }

        // Restore categories checkboxes
        const categoryCheckboxes = document.querySelectorAll<HTMLInputElement>('input[id$="-category"]');

        categoryCheckboxes.forEach((checkbox) => {
            checkbox.checked = snapshot.categories.includes(checkbox.value);
        });

        // Restore tinymce description
        const editor = window.tinymce.get("asset-description") as any;

        if (editor?.setContent) {
            editor.setContent(snapshot.description);
        }

        // Restore conditional UI (password field visibility).
        this._handleViewAccessChange();
    }

    private _initializeSoftwareSelector(): void {
        const photogrammetrySoftwares = [
            "Agisoft MetaShape",
            "3DF Zephyr",
            "Reality Capture",
            "Pix4D",
            "DJI Terra",
            "Trimble Inpho",
            "DroneDeploy",
            "Autodesk ReCap",
            "Bentley ContextCapture",
            "MatterPort",
            "COLMAP",
            "Meshroom",
            "MicMac",
            "Other"
        ];

        const tiledModelSoftwares = [
            "Agisoft MetaShape",
            "Reality Capture",
            "DJI Terra",
            "Bentley ContextCapture",
            "Other"
        ];

        const pointCloudSoftwares = [
            "Leica: Cyclone",
            "Faro: Scene",
            "Riegl: RiSCAN PRO",
            "Trimble: RealWorks",
            "Autodesk: ReCap",
            "Bentley: Pointools",
            "MatterPort",
            "Other"
        ];

        const orthomosaicSoftwares = [
            "Agisoft MetaShape",
            "Reality Capture",
            "DJI Terra",
            "Bentley ContextCapture",
            "Other"
        ];

        const softwareSelector = document.getElementById("software-selector") as HTMLSelectElement | null;
        const assetType = this._CONSTRUKTED_AJAX.asset_type;

        if (!softwareSelector) {
            return;
        }

        if (assetType === ConstruktedAssetType.PolygonMesh) {
            this._populateSoftwareSelector(softwareSelector, photogrammetrySoftwares);
        } else if (assetType === ConstruktedAssetType.ThreeDTiles) {
            this._populateSoftwareSelector(softwareSelector, tiledModelSoftwares);
        } else if (assetType === ConstruktedAssetType.PointCloud) {
            this._populateSoftwareSelector(softwareSelector, pointCloudSoftwares);
        } else if (assetType === ConstruktedAssetType.Orthomosaic) {
            this._populateSoftwareSelector(softwareSelector, orthomosaicSoftwares);
        }

        if (this._CONSTRUKTED_AJAX.software) {
            softwareSelector.value = this._CONSTRUKTED_AJAX.software;
        }
    }

    private _populateSoftwareSelector(selector: HTMLSelectElement, softwareList: string[]): void {
        selector.innerHTML = "";

        const defaultOption = document.createElement("option");

        defaultOption.value = "";
        defaultOption.textContent = "Select software...";
        selector.appendChild(defaultOption);

        softwareList.forEach((software) => {
            const option = document.createElement("option");

            option.value = software;
            option.textContent = software;
            selector.appendChild(option);
        });
    }

    private _bindEvents(): void {
        const jQuery = window.jQuery;
        const panel = this;

        jQuery(document).on("click", ".close-editor", () => {
            window.panelManager?.closePanel("asset-details-editor");
        });
        jQuery(document).on("click", ".save-asset-details", (e: any) => panel._handleSaveAssetDetails(e));
        jQuery(document).on("change", 'input[name="view-access"]', () => this._handleViewAccessChange());
    }

    private _handleViewAccessChange(): void {
        const selectedValue = document.querySelector<HTMLInputElement>('input[name="view-access"]:checked')?.value;
        const passwordField = document.getElementById("password-protection-field") as HTMLElement | null;

        if (!passwordField) {
            console.warn('AssetDetailsEditorPanel._handleViewAccessChange: "#password-protection-field" not found');

            return;
        }

        if (selectedValue === "password") {
            passwordField.style.display = "block";
        } else {
            passwordField.style.display = "none";
        }
    }

    private static _getSelectedCategories(): string[] {
        const categories: string[] = [];
        const checkboxes = document.querySelectorAll<HTMLInputElement>('input[id$="-category"]:checked');

        checkboxes.forEach((checkbox) => {
            categories.push(checkbox.value);
        });

        return categories;
    }

    private _handleSaveAssetDetails(e: JQuery.TriggeredEvent): void {
        e.preventDefault();

        const button = e.target as HTMLButtonElement;

        if (button.disabled) {
            return;
        }

        button.disabled = true;

        const prevStatus = this._initialStatus;

        const statusElement = document.querySelector<HTMLInputElement>('input[name="asset-status"]:checked');
        const status = statusElement?.value;
        const isPublished = status === "1";

        if (isPublished) {
            const titleElement = document.getElementById("asset-title") as HTMLInputElement;
            const title = titleElement?.value.trim();

            if (!title) {
                alert("Asset title is required");
                titleElement?.focus();
                button.disabled = false;

                return;
            }

            const description = window.tinymce.get("asset-description")?.getContent().trim();

            if (!description) {
                alert("Asset description is required");
                button.disabled = false;

                return;
            }

            const captureDateElement = document.getElementById("asset-capture-date") as HTMLInputElement;

            if (!captureDateElement?.value) {
                alert("Capture date is required");
                captureDateElement?.focus();
                button.disabled = false;

                return;
            }

            const categories = AssetDetailsEditorPanel._getSelectedCategories();

            if (categories.length === 0) {
                alert("Please select at least one category");
                button.disabled = false;

                return;
            }

            const viewAccess = document.querySelector('input[name="view-access"]:checked');

            if (!viewAccess) {
                alert("View access is required");
                button.disabled = false;

                return;
            }

            const downloadAccess = document.querySelector('input[name="download-access"]:checked');

            if (!downloadAccess) {
                alert("Download access is required");
                button.disabled = false;

                return;
            }

            const coordinateSystem = document.querySelector('input[name="coordinate-system"]:checked');

            if (!coordinateSystem) {
                alert("Coordinate system is required");
                button.disabled = false;

                return;
            }
        }

        const formElements = this._getFormElements();
        const formData = this._createFormData(formElements, button.dataset.postId);

        this._submitFormData(formData, button, prevStatus);
    }

    private _getFormElements(): FormElements {
        const title = document.getElementById("asset-title") as HTMLInputElement;
        const captureDate = document.getElementById("asset-capture-date") as HTMLInputElement;
        const tags = document.getElementById("asset-tags") as HTMLInputElement;
        const assetPassword = document.getElementById("asset-password") as HTMLInputElement;

        const emptyInput = document.createElement("input");
        const emptySelect = document.createElement("select");

        const viewAccess = document.querySelector<HTMLInputElement>('input[name="view-access"]:checked') || emptyInput;
        const downloadAccess =
            document.querySelector<HTMLInputElement>('input[name="download-access"]:checked') || emptyInput;
        const coordinateSystem =
            document.querySelector<HTMLInputElement>('input[name="coordinate-system"]:checked') || emptyInput;
        const status = document.querySelector<HTMLInputElement>('input[name="asset-status"]:checked') || emptyInput;
        const software = (document.getElementById("software-selector") as HTMLSelectElement) || emptySelect;

        return {
            viewAccess,
            title,
            captureDate,
            tags,
            downloadAccess,
            coordinateSystem,
            status,
            assetPassword,
            software
        };
    }

    private _createFormData(elements: FormElements, postId: string | undefined): FormDataType {
        const formData: FormDataType = {
            action: "save_asset_details",
            post_id: postId,
            title: elements.title.value,
            description: window.tinymce.get("asset-description")?.getContent() || "",
            captureDate: elements.captureDate.value,
            categories: AssetDetailsEditorPanel._getSelectedCategories(),
            tags: elements.tags.value,
            viewAccess: elements.viewAccess.value,
            downloadAccess: elements.downloadAccess.value,
            coordinateSystem: elements.coordinateSystem.value,
            status: elements.status.value,
            software: elements.software?.value || ""
        };

        if (elements.viewAccess.value === "password" && elements.assetPassword) {
            formData.assetPassword = elements.assetPassword.value;
        }

        return formData;
    }

    private _submitFormData(formData: FormDataType, button: HTMLButtonElement, prevStatus?: string): void {
        const panel = this;

        window.jQuery.ajax({
            url: this._CONSTRUKTED_AJAX.ajaxurl,
            type: "POST",
            data: formData,
            success: (response: AjaxResponse) => {
                if (response.success) {
                    alert("Asset details saved successfully!");
                    window.panelManager?.closePanelInternal("asset-details-editor");

                    if (prevStatus === "0" && response.data.new_status === "publish") {
                        window.location.reload();

                        return;
                    }

                    const currentUrl = window.location.href;
                    const baseUrl = currentUrl.split("?")[0];

                    window.location.href = baseUrl;

                    const data = response.data;
                    const popup = window.jQuery("#construkted-popup-info");

                    popup.find("h2 > span:first").text(data.title);
                    popup.find(".post-content").html(data.description);

                    if (data.categories && data.categories.length > 0) {
                        const categoriesHtml = `<ul class="mb-2 nostyle">${data.categories
                            .map(
                                (category) =>
                                    `<li class="term-${category}">
                                        <a href="/video-category/${category}/">${
                                        category.charAt(0).toUpperCase() + category.slice(1)
                                    }</a>
                                    </li>`
                            )
                            .join("")}</ul>`;

                        popup.find(".make-flex-row .make-flex").html(categoriesHtml);
                    }

                    if (data.date_capture) {
                        const dateMetadata = `
                            <div class="metadata-item">
                                <strong class="text-sm">Date of capture</strong>
                                ${data.date_capture}
                            </div>`;

                        let metadataList = popup.find(".metadata-list");

                        if (!metadataList.length) {
                            popup.find(".asset-metadata-inner").html('<div class="metadata-list"></div>');
                            metadataList = popup.find(".metadata-list");
                        }

                        const existingDateItem = metadataList.find(
                            ".metadata-item:has(strong:contains('Date of capture'))"
                        );

                        if (existingDateItem.length) {
                            existingDateItem.replaceWith(dateMetadata);
                        } else {
                            metadataList.prepend(dateMetadata);
                        }
                    }

                    if (data.download_access) {
                        const downloadLink = popup.find("a[download]");

                        if (data.download_access !== "allow_download") {
                            downloadLink.parent("div").hide();
                        } else {
                            downloadLink.parent("div").show();
                        }
                    }

                    if (data.software) {
                        panel._CONSTRUKTED_AJAX.software = data.software;
                    }
                } else {
                    alert("An error occurred while saving the asset details. Please try again.");
                }

                button.disabled = false;
            },
            error: (error: unknown) => {
                console.error("Error saving asset details:", error);
                button.disabled = false;
                alert("An error occurred while saving the asset details. Please try again.");
            }
        });
    }
}
